import React, { useState, useEffect } from 'react';
import { 
  Container, Card, Button, Alert, Spinner, InputGroup, Form, Row, Col, Table, Badge
} from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { FaSearch, FaFilter, FaSortAmountDown, FaArrowLeft, FaEye } from 'react-icons/fa';
import MainLayout from '../../layouts/MainLayout';
import mockRnDService from '../../services/mockRnDService';

const FinishedProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('end_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    setError(null);

    try {
      const finishedProjects = await mockRnDService.getFinishedProjects();
      setProjects(finishedProjects);
    } catch (err) {
      console.error("Error fetching projects:", err);
      setError("Terjadi kesalahan saat memuat data proyek.");
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = () => {
    return projects
      .filter(project => {
        // Apply search term filter
        return project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
               project.description.toLowerCase().includes(searchTerm.toLowerCase());
      })
      .sort((a, b) => {
        // Apply sorting
        let comparison = 0;
        
        if (sortBy === 'name') {
          comparison = a.name.localeCompare(b.name);
        } else if (sortBy === 'end_date') {
          comparison = new Date(b.end_date || b.target_date) - new Date(a.end_date || a.target_date);
        } else if (sortBy === 'start_date') {
          comparison = new Date(b.start_date) - new Date(a.start_date);
        }
        
        return sortOrder === 'asc' ? -comparison : comparison;
      });
  };

  return (
    <MainLayout>
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Daftar Proyek R&D Selesai</h2>
          <div>
            <Button variant="primary" as={Link} to="/dashboard/rnd">
              <FaArrowLeft className="me-1" /> Kembali ke Dashboard
            </Button>
          </div>
        </div>

        {error && <Alert variant="warning">{error}</Alert>}

        {/* Search and Filters */}
        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={6} className="mb-3 mb-md-0">
                <InputGroup>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Cari berdasarkan nama atau deskripsi..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={6}>
                <InputGroup>
                  <InputGroup.Text>
                    <FaSortAmountDown />
                  </InputGroup.Text>
                  <Form.Select
                    value={`${sortBy}-${sortOrder}`}
                    onChange={(e) => {
                      const [newSortBy, newSortOrder] = e.target.value.split('-');
                      setSortBy(newSortBy);
                      setSortOrder(newSortOrder);
                    }}
                  >
                    <option value="end_date-desc">Tanggal Selesai (Terbaru)</option>
                    <option value="end_date-asc">Tanggal Selesai (Terlama)</option>
                    <option value="name-asc">Nama (A-Z)</option>
                    <option value="name-desc">Nama (Z-A)</option>
                    <option value="start_date-desc">Tanggal Mulai (Terbaru)</option>
                    <option value="start_date-asc">Tanggal Mulai (Terlama)</option>
                  </Form.Select>
                </InputGroup>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Project List */}
        <Card>
          <Card.Header className="bg-white">
            <h4>Proyek Selesai ({filteredProjects().length})</h4>
          </Card.Header>
          <Card.Body>
            {loading ? (
              <div className="text-center p-5">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="mt-3">Memuat data proyek...</p>
              </div>
            ) : (
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>Nama Proyek</th>
                    <th>Tanggal Mulai</th>
                    <th>Tanggal Selesai</th>
                    <th>Manager</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects().length > 0 ? (
                    filteredProjects().map((project) => (
                      <tr key={project.id}>
                        <td>
                          <Link to={`/rnd/projects/${project.id}`}>
                            <strong>{project.name}</strong>
                          </Link>
                          <div className="text-muted small">{project.description}</div>
                        </td>
                        <td>{project.start_date}</td>
                        <td>
                          <Badge bg="success">
                            {project.end_date || project.target_date}
                          </Badge>
                        </td>
                        <td>{project.manager}</td>
                        <td>
                          <Button 
                            variant="outline-info" 
                            size="sm" 
                            as={Link} 
                            to={`/rnd/projects/${project.id}`}
                          >
                            <FaEye /> Detail
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center py-4">
                        Tidak ada proyek selesai saat ini.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </Container>
    </MainLayout>
  );
};

export default FinishedProjects;